<script>
/**
 * O cartão que PROPÕE gravar quem é a pessoa (spec-009, US-3/AC-3.2).
 *
 * ★ **Burro de propósito.** Ele não lê store, não chama API e não sabe o que é
 * memória: recebe uma proposta, deixa editar e emite `confirmar` com o que
 * está na tela. Quem grava é o mixin da view — e é isso que torna a regra do
 * AC-3.3 verificável: não existe caminho daqui até a API que não passe pelo
 * `@click` de um botão.
 *
 * ★ **Os campos são EDITÁVEIS, e isso é parte do desenho.** A proposta vem de
 * heurística sobre o texto (`utils/apresentacao-do-profissional.js`), e a
 * transcrição de voz erra nome próprio. Propor um rascunho corrigível é o que
 * permite ser útil sem ser presunçoso — o que a pessoa confirma é o que ela
 * leu, não o que a máquina adivinhou.
 */
import { CONFIRMAR_PERFIL } from '@modules/aawz-intelligence-v2/constants/TextChat'

export default {
  name: 'IntelligenceV2ConfirmarPerfilCard',
  props: {
    /** `{nome, papel, objetivo}` — o rascunho que a leitura do turno montou. */
    proposta: {
      type: Object,
      required: true,
    },
    /** Gravação em voo: trava os dois botões e os campos. */
    salvando: {
      type: Boolean,
      default: false,
    },
    /** Motivo da última falha, já em PT-BR — o pai decide o texto. */
    erro: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      texts: CONFIRMAR_PERFIL,
      nome: '',
      papel: '',
      objetivo: '',
    }
  },
  computed: {
    /**
     * Sem nenhum campo preenchido não há o que guardar — o botão fica inerte
     * COM o motivo ao lado, nunca um clique que não faz nada (precedente
     * [G-12] do fee-fixo: "botão desabilitado é o beco visível").
     */
    vazio() {
      return !this.nome.trim() && !this.papel.trim() && !this.objetivo.trim()
    },
    travado() {
      return this.salvando || this.vazio
    },
  },
  watch: {
    // A proposta troca quando a pessoa se apresenta de novo (ela corrigiu algo
    // que a leitura errou). Reaplicar é o certo: o rascunho é do último turno.
    proposta: {
      immediate: true,
      handler: 'aplicarProposta',
    },
  },
  methods: {
    aplicarProposta(proposta) {
      const p = proposta || {}
      this.nome = p.nome || ''
      this.papel = p.papel || ''
      this.objetivo = p.objetivo || ''
    },
    confirmar() {
      if (this.travado) return
      this.$emit('confirmar', {
        nome: this.nome.trim(),
        papel: this.papel.trim(),
        objetivo: this.objetivo.trim(),
      })
    },
  },
}
</script>

<template>
  <div
    class="ia-block-card confirmar-perfil-card"
    role="group"
    :aria-label="texts.TITULO"
    data-teste="confirmar-perfil-card"
  >
    <div class="ia-block-card__cabecalho">
      <icon-svg name="user-2" :size="14" color="var(--primary)" class="ia-block-card__icone" />
      <span class="ia-block-card__titulo">{{ texts.TITULO }}</span>
    </div>

    <div class="ia-block-card__corpo">
      <p class="az-ds-aux mb-3">{{ texts.DESCRICAO }}</p>

      <div class="d-flex flex-column az-gap-small">
        <input-by-type
          v-model="nome"
          type="text"
          :label="texts.CAMPO_NOME"
          :disabled="salvando"
          data-test="confirmar-perfil-nome"
        />
        <input-by-type
          v-model="papel"
          type="text"
          :label="texts.CAMPO_PAPEL"
          :disabled="salvando"
          data-test="confirmar-perfil-papel"
        />
        <input-by-type
          v-model="objetivo"
          type="text"
          :label="texts.CAMPO_OBJETIVO"
          :disabled="salvando"
          data-test="confirmar-perfil-objetivo"
        />
      </div>

      <p v-if="erro" class="confirmar-perfil-card__erro mt-3 mb-0" role="alert" data-teste="erro">
        {{ erro }}
      </p>

      <div class="d-flex align-center az-gap-small mt-4">
        <BaseButton underlined :disabled="salvando" @click="$emit('dispensar')">
          {{ texts.DISPENSAR }}
        </BaseButton>
        <BaseButton
          primary
          :loading="salvando"
          :disabled="travado"
          data-test="confirmar-perfil-salvar"
          @click="confirmar"
        >
          {{ texts.SALVAR }}
        </BaseButton>
        <span v-if="vazio && !salvando" class="az-ds-aux confirmar-perfil-card__motivo">
          {{ texts.VAZIO }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Só o que não tem utilitário equivalente: espaçamento e layout saem de
// `mb-3`/`mt-4`/`d-flex`/`az-gap-small` no template (regra do repo — nunca CSS
// custom para o que o Vuetify/AAWZ já entrega). O que sobra é COR, que é
// token, e o motivo da trava, que fica ao lado do botão inerte porque é ali
// que o olho está quando o clique não acontece.
.confirmar-perfil-card__erro
  color: var(--danger-text)

.confirmar-perfil-card__motivo
  color: var(--neutral-600)
</style>
